import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Flame, Loader2, Check, Trash2, Sparkles, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { DecreeList } from "@/components/DecreeList";

export default function PlanDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [openDay, setOpenDay] = useState(null);

  const load = () => api.get(`/plans/${id}`).then((r) => { setPlan(r.data); setOpenDay(r.data.current_day); }).catch(() => setError(true));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const advance = async () => {
    setAdvancing(true);
    try {
      const { data } = await api.post(`/plans/${id}/advance`);
      setPlan(data); setOpenDay(data.current_day);
      toast.success(`Day ${data.current_day} is ready!`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not unlock the next day.");
    } finally { setAdvancing(false); }
  };

  const complete = async (day) => {
    setCompleting(true);
    try {
      const { data } = await api.post(`/plans/${id}/complete`, { day });
      setPlan(data);
      toast.success("Day complete — keep the streak going!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not mark complete.");
    } finally { setCompleting(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this plan? This cannot be undone.")) return;
    await api.delete(`/plans/${id}`);
    toast.success("Plan deleted");
    navigate("/plans");
  };

  if (error) return <div className="max-w-3xl mx-auto px-6 pt-24 text-center"><p className="font-decree text-xl text-[#9B3B3B]">Plan not found.</p><Link to="/plans" className="text-[#9B7E4E] underline mt-4 inline-block">Back to plans</Link></div>;
  if (!plan) return <div className="text-center pt-32"><Loader2 className="w-8 h-8 text-[#2C2A29] animate-spin mx-auto" /></div>;

  const pct = Math.round((plan.completed_count / plan.total_days) * 100);
  const finished = plan.current_day >= plan.total_days && plan.completed_count >= plan.total_days;
  const dayNums = Object.keys(plan.days).map(Number).sort((a, b) => b - a);

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 pt-12 sm:pt-16 pb-24" data-testid="plan-detail">
      <div className="flex items-center justify-between">
        <Link to="/plans" data-testid="plan-back" className="flex items-center gap-2 text-sm text-[#7A756E] hover:text-[#2C2A29] transition-colors duration-300">
          <ArrowLeft className="w-4 h-4" /> All plans
        </Link>
        <button onClick={remove} data-testid="plan-delete" aria-label="Delete plan" className="text-[#B7AE9F] hover:text-[#9B3B3B] transition-colors duration-300 p-1">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="flex items-end justify-between gap-4 mt-6">
        <div>
          <p className="font-body text-xs uppercase tracking-[0.2em] text-[#7A756E]">{plan.mode === "mix" ? "Guided journey" : "Themed plan"}</p>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] tracking-tight mt-1">{plan.title}</h1>
        </div>
        {plan.streak > 0 && (
          <span data-testid="plan-streak" className="flex items-center gap-1.5 text-[#9B7E4E] font-heading text-2xl shrink-0">
            <Flame className="w-6 h-6" strokeWidth={2} /> {plan.streak}
          </span>
        )}
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between font-body text-sm text-[#4A4744] mb-2">
          <span data-testid="plan-progress-label">Day {plan.current_day} of {plan.total_days}</span>
          <span>{plan.completed_count} completed · {plan.per_day}/day</span>
        </div>
        <div className="h-2 bg-[#EAE6DF] rounded-full overflow-hidden">
          <div className="h-full bg-[#9B7E4E] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} data-testid="plan-progress-bar" />
        </div>
      </div>

      {plan.can_advance && (
        <button onClick={advance} disabled={advancing} data-testid="plan-advance-button"
          className="mt-8 w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#2C2A29] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300 disabled:opacity-50">
          {advancing ? <><Loader2 className="w-4 h-4 animate-spin" /> Preparing Day {plan.current_day + 1}…</> : <><Sparkles className="w-4 h-4" /> Begin Day {plan.current_day + 1}</>}
        </button>
      )}

      {finished && (
        <div className="mt-8 border border-[#9B7E4E] bg-[#F3F0EA] rounded-sm p-6 text-center" data-testid="plan-finished">
          <p className="font-heading text-2xl text-[#2C2A29]">You finished the journey! 🎉</p>
          <p className="font-decree text-lg text-[#4A4744] mt-2">You established {plan.total_days} days of decrees. Well done, faithful one.</p>
        </div>
      )}

      {/* Days — newest first */}
      <div className="mt-10 space-y-4">
        {dayNums.map((n) => {
          const day = plan.days[String(n)];
          const isOpen = openDay === n;
          return (
            <div key={n} data-testid={`plan-day-${n}`} className="border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm overflow-hidden">
              <button onClick={() => setOpenDay(isOpen ? null : n)} className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-[#F3F0EA] transition-colors duration-300">
                <span className="flex items-center gap-3">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-body ${day.completed ? "bg-[#9B7E4E] text-[#FAF9F6]" : "border border-[#D9D4CC] text-[#7A756E]"}`}>
                    {day.completed ? <Check className="w-4 h-4" /> : n}
                  </span>
                  <span>
                    <span className="block font-heading text-lg text-[#2C2A29]">Day {n}</span>
                    <span className="block font-body text-sm text-[#7A756E] capitalize">{day.topic}</span>
                  </span>
                </span>
                <ChevronDown className={`w-5 h-5 text-[#B7AE9F] transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="px-5 pb-6 pt-1 border-t border-[#EAE6DF]">
                  <DecreeList session={{ intro: `Speak these ${day.topic} decrees aloud today.`, topic: day.topic, decrees: day.decrees, word_study: day.word_study }} />
                  {!day.completed && (
                    <button onClick={() => complete(n)} disabled={completing} data-testid={`plan-complete-${n}`}
                      className="mt-8 w-full flex items-center justify-center gap-2 px-6 py-3 border border-[#9B7E4E] text-[#2C2A29] rounded-sm hover:bg-[#F3F0EA] transition-colors duration-300 disabled:opacity-50">
                      {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Mark Day {n} complete
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
